import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import MOMABffService from '@/services/moma-bff.service';
import {
  NpsMonthlyChartDto,
  NpsMonthlyRequestDto,
} from '@/data/dto/nps/npsEvolutions.dto';
import { NpsTargetIndicatorsDto } from '@/data/dto/nps/npsTargetIndicators.dto';
import { NpsRecommendationProfilesDto } from '@/data/dto/nps/npsRecommendationsProfile.dto';
import { NpsBrandIndicatorsDto, NpsImageDto } from '@/data/dto/nps/npsDiagnosis.dto';
import { InsightsNpsSettingsDTO } from '@/data/dto/nps/npsInsightsSettings.dto';
import DateUtil from '@/utils/date.utils';

const service = new MOMABffService();
type NpsSection =
  | 'monthlyChart'
  | 'targetIndicators'
  | 'recommendationProfile'
  | 'diagnosisIndicators'
  | 'diagnosisImage';

@Module
export default class NpsModule extends VuexModule {
  npsMonthlyChart: NpsMonthlyChartDto | null = null;
  npsTargetIndicators: NpsTargetIndicatorsDto | null = null;
  npsRecommendationProfile: NpsRecommendationProfilesDto | null = null;
  npsDiagnosisIndicators: NpsBrandIndicatorsDto | null = null;
  npsDiagnosisImage: NpsImageDto | null = null;
  npsInsightsSettings: InsightsNpsSettingsDTO | null = null;

  loading = {
    monthlyChart: false,
    targetIndicators: false,
    recommendationProfile: false,
    diagnosisIndicators: false,
    diagnosisImage: false,
    insightsSettings: false,
  };

  errors = {
    monthlyChart: false,
    targetIndicators: false,
    recommendationProfile: false,
    diagnosisIndicators: false,
    diagnosisImage: false,
  };

  @Action
  async fetchNpsTargetIndicators(): Promise<NpsTargetIndicatorsDto | undefined> {
    if (this.loading.targetIndicators) return;
    try {
      this.context.commit('setTargetIndicatorsLoading', true);
      this.context.commit('setNpsSectionError', {
        section: 'targetIndicators',
        value: false,
      });
      this.context.commit('setNpsTargetIndicators', null);

      const response = await service.getTargetIndicatorsNps();
      this.context.commit('setNpsTargetIndicators', response);
      return response;
    } catch {
      this.context.commit('setNpsSectionError', {
        section: 'targetIndicators',
        value: true,
      });
      this.context.commit(
        'showAlert',
        { message: 'Erro ao carregar indicadores de meta do NPS', type: 'danger' },
        { root: true }
      );
    } finally {
      this.context.commit('setTargetIndicatorsLoading', false);
    }
  }

  @Action
  async fetchNpsMonthlyChart(): Promise<NpsMonthlyChartDto | undefined> {
    if (this.loading.monthlyChart) return;
    try {
      this.context.commit('setMonthlyChartLoading', true);
      this.context.commit('setNpsSectionError', {
        section: 'monthlyChart',
        value: false,
      });
      this.context.commit('setNpsMonthlyChart', null);

      const response = await service.getMonthlyChartNps(
        DateUtil.lastTwelveMonthsRange()
      );
      this.context.commit('setNpsMonthlyChart', response);
      return response;
    } catch {
      this.context.commit('setNpsSectionError', {
        section: 'monthlyChart',
        value: true,
      });
      this.context.commit(
        'showAlert',
        { message: 'Erro ao carregar evolução mensal do NPS', type: 'danger' },
        { root: true }
      );
    } finally {
      this.context.commit('setMonthlyChartLoading', false);
    }
  }

  @Action
  async fetchNpsRecommendationProfile(queryParams: {
    month: number;
    year: number;
  }): Promise<void> {
    if (this.loading.recommendationProfile) return;
    try {
      this.context.commit('setRecommendationProfileLoading', true);
      this.context.commit('setNpsSectionError', {
        section: 'recommendationProfile',
        value: false,
      });
      this.context.commit('setNpsRecommendationProfile', null);

      const response = await service.getRecommendationProfileNps(queryParams);
      this.context.commit('setNpsRecommendationProfile', response);
    } catch {
      this.context.commit('setNpsSectionError', {
        section: 'recommendationProfile',
        value: true,
      });
      this.context.commit(
        'showAlert',
        { message: 'Erro ao carregar perfil de recomendação do NPS', type: 'danger' },
        { root: true }
      );
    } finally {
      this.context.commit('setRecommendationProfileLoading', false);
    }
  }

  @Action
  async fetchNpsDiagnosisIndicators(params: NpsMonthlyRequestDto): Promise<void> {
    if (this.loading.diagnosisIndicators) return;
    try {
      this.context.commit('setNpsSectionLoading', {
        section: 'diagnosisIndicators',
        value: true,
      });
      this.context.commit('setNpsSectionError', {
        section: 'diagnosisIndicators',
        value: false,
      });
      this.context.commit('setNpsDiagnosisIndicators', null);

      const response = await service.getDiagnosisIndicatorsNps(params);
      this.context.commit('setNpsDiagnosisIndicators', response);
    } catch {
      this.context.commit('setNpsSectionError', {
        section: 'diagnosisIndicators',
        value: true,
      });
      this.context.commit(
        'showAlert',
        {
          message: 'Erro ao carregar os indicadores de diagnóstico do NPS',
          type: 'danger',
        },
        { root: true }
      );
    } finally {
      this.context.commit('setNpsSectionLoading', {
        section: 'diagnosisIndicators',
        value: false,
      });
    }
  }

  @Action
  async fetchNpsDiagnosisImage(params: NpsMonthlyRequestDto): Promise<void> {
    if (this.loading.diagnosisImage) return;
    try {
      this.context.commit('setNpsSectionLoading', {
        section: 'diagnosisImage',
        value: true,
      });
      this.context.commit('setNpsSectionError', {
        section: 'diagnosisImage',
        value: false,
      });
      this.context.commit('setNpsDiagnosisImage', null);

      const response = await service.getDiagnosisImageNps(params);
      this.context.commit('setNpsDiagnosisImage', response);
    } catch {
      this.context.commit('setNpsSectionError', {
        section: 'diagnosisImage',
        value: true,
      });
      this.context.commit(
        'showAlert',
        {
          message: 'Erro ao carregar os atributos de imagem do NPS',
          type: 'danger',
        },
        { root: true }
      );
    } finally {
      this.context.commit('setNpsSectionLoading', {
        section: 'diagnosisImage',
        value: false,
      });
    }
  }

  @Action
  async fetchNpsInsightsSettings(): Promise<
    InsightsNpsSettingsDTO | undefined
  > {
    try {
      this.context.commit('setInsightsSettingsLoading', true);

      const response = await service.getInsightsNpsSettings();
      this.context.commit('setNpsInsightsSettings', response);
      return response;
    } catch {
      this.context.commit(
        'showAlert',
        {
          message: 'Erro ao carregar configurações de insights do NPS',
          type: 'danger',
        },
        { root: true }
      );
      return undefined;
    } finally {
      this.context.commit('setInsightsSettingsLoading', false);
    }
  }

  @Action
  async saveInsightsNpsSettings(
    settings: InsightsNpsSettingsDTO
  ): Promise<InsightsNpsSettingsDTO | undefined> {
    try {
      this.context.commit('setInsightsSettingsLoading', true);
      await service.saveInsightsNpsSettings(settings);
      this.context.commit('setNpsInsightsSettings', settings);
      this.context.commit(
        'showAlert',
        {
          message: 'Configurações de insights do NPS atualizadas com sucesso!',
          type: 'success',
        },
        { root: true }
      );
      return settings;
    } catch {
      this.context.commit(
        'showAlert',
        {
          message: 'Erro ao atualizar configurações de insights do NPS',
          type: 'danger',
        },
        { root: true }
      );
      return undefined;
    } finally {
      this.context.commit('setInsightsSettingsLoading', false);
    }
  }

  @Mutation
  setNpsSectionError({ section, value }: { section: NpsSection; value: boolean }): void {
    this.errors[section] = value;
  }

  @Mutation
  setNpsSectionLoading({ section, value }: { section: NpsSection; value: boolean }): void {
    this.loading[section] = value;
  }

  @Mutation
  setNpsDiagnosisIndicators(data: NpsBrandIndicatorsDto | null): void {
    this.npsDiagnosisIndicators = data;
  }

  @Mutation
  setNpsDiagnosisImage(data: NpsImageDto | null): void {
    this.npsDiagnosisImage = data;
  }

  @Mutation
  setNpsMonthlyChart(data: NpsMonthlyChartDto | null): void {
    this.npsMonthlyChart = data;
  }

  @Mutation
  setNpsTargetIndicators(data: NpsTargetIndicatorsDto | null): void {
    this.npsTargetIndicators = data;
  }

  @Mutation
  setNpsRecommendationProfile(
    data: NpsRecommendationProfilesDto | null
  ): void {
    this.npsRecommendationProfile = data;
  }

  @Mutation
  setMonthlyChartLoading(value: boolean): void {
    this.loading.monthlyChart = value;
  }

  @Mutation
  setTargetIndicatorsLoading(value: boolean): void {
    this.loading.targetIndicators = value;
  }

  @Mutation
  setRecommendationProfileLoading(value: boolean): void {
    this.loading.recommendationProfile = value;
  }

  @Mutation
  setNpsInsightsSettings(data: InsightsNpsSettingsDTO | null): void {
    this.npsInsightsSettings = data;
  }

  @Mutation
  setInsightsSettingsLoading(value: boolean): void {
    this.loading.insightsSettings = value;
  }
}
